import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { GraduationCap, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
// import GoogleSignIn from '../../components/GoogleSignIn';
import { motion } from 'framer-motion';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('student');
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        navigate(data.role === 'admin' ? '/admin' : '/student');
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 font-sans selection:bg-lime-400/30 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-lime-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full z-10 space-y-8 bg-neutral-900/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8"
      >
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>

        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-neutral-800 border border-neutral-700 shadow-md">
            <GraduationCap className="h-8 w-8 text-lime-400" />
          </div>
          <h2 className="mt-6 text-3xl font-semibold tracking-tight text-white">
            Sign in to EduPortal
          </h2>
          <p className="mt-2 text-sm text-neutral-400">
            Or{" "}
            <Link
              to="/register"
              className="font-medium text-lime-400 hover:text-lime-300 transition-colors"
            >
              create a new account
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-1">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`w-full bg-neutral-950/50 border ${errors.email ? "border-red-500" : "border-neutral-800"} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-4 transition-all`}
                  placeholder="Enter your email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-neutral-500" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className={`w-full bg-neutral-950/50 border ${errors.password ? "border-red-500" : "border-neutral-800"} text-white placeholder-neutral-500 focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 pl-10 pr-10 transition-all`}
                  placeholder="Enter your password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-neutral-500 hover:text-neutral-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Role Selector */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-neutral-300 mb-1">
                Login as
              </label>
              <select
                id="role"
                className={`w-full bg-neutral-950/50 border ${errors.role ? "border-red-500" : "border-neutral-800"} text-white focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400 rounded-lg py-2.5 px-4 transition-all appearance-none`}
                {...register("role", { required: "Please select a role" })}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="" className="bg-neutral-900">Select role</option>
                <option value="student" className="bg-neutral-900">Student</option>
                <option value="admin" className="bg-neutral-900">Admin</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-400">{errors.role.message}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-neutral-950 bg-lime-400 hover:bg-lime-300 font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-500 focus:ring-offset-neutral-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-950 mr-2"></div>
                  Signing in...
                </div>
              ) : (
                "Sign in"
              )}
            </button>
          </div>

          {/* Google Sign-In placeholder - if uncommented in the future */}
          {/* <div className="mt-6 border-t border-white/10 pt-6">
            <GoogleSignIn mode="login" role={selectedRole} className="w-full border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg py-3 px-4" />
          </div> */}
        </form>
      </motion.div>
    </div>
  );
};

export default Login;
